import { captureDueLabel } from '@goaltree/backend/convex/lib/captureDates'
import { formatAmount } from '@goaltree/backend/convex/lib/habitMeasure'
import { type LiveItem, liveToCaptureItem } from '@goaltree/backend/convex/lib/livePreview'
import { recursDaysLabel } from '@goaltree/backend/convex/lib/schedule'
import { cn } from '@goaltree/ui/lib/utils'
import i18next, { type TFunction } from 'i18next'
import {
  Calendar,
  CalendarCheck,
  CalendarDays,
  Check,
  CircleCheck,
  Flag,
  Lock,
  type LucideIcon,
  MapPin,
  Plus,
  Repeat,
  X,
} from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { type ReactNode, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { measureChipLabel } from '@/components/number-habit'
import { PlaceInput } from '@/components/place-input'
import { useStickToEnd } from '@/hooks/use-stick-to-end'
import type { CaptureEdit, CaptureItem } from '@/hooks/use-voice-goals'
import { dayLabel, hhmm, localDateTime } from '@/lib/events'
import { openHabitUpsell } from '@/lib/usage-limit'

const KNOWN_CATEGORIES = new Set([
  'health',
  'career',
  'mind',
  'finance',
  'relationships',
  'creative',
])

const KIND_ICON = { goal: Flag, task: CircleCheck, event: CalendarDays, habit: Repeat } as const

const KIND_TEXT: Record<CaptureItem['kind'], string> = {
  goal: 'text-primary-text',
  task: 'text-sky-600 dark:text-sky-400',
  event: 'text-muted-foreground',
  habit: 'text-emerald-600 dark:text-emerald-400',
}

export const KIND_PILL: Record<CaptureItem['kind'], string> = {
  goal: 'bg-primary/15 text-primary-text',
  task: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  event: 'bg-muted text-muted-foreground',
  habit: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
}

function categoryLabel(t: TFunction, category: string) {
  const key = category.toLowerCase()
  return t(`goals.cat.${key}`, { defaultValue: category })
}

function CategoryDot({ category }: { category: string }) {
  const key = category.toLowerCase()
  return (
    <span
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{
        backgroundColor: KNOWN_CATEGORIES.has(key) ? `var(--${key})` : 'var(--muted-foreground)',
      }}
    />
  )
}

const kindLabel = (t: TFunction, kind: CaptureItem['kind']) =>
  kind === 'event' ? t('events.kindEvent') : t(`voice.kind_${kind}`)

// "Mon, Nov 9 · All day" / "Thu, Oct 29 · 15:00–15:45".
function eventWhenLabel(t: TFunction, item: CaptureItem & { dueDate: string }) {
  const start = localDateTime(item.dueDate, item.allDay ? null : item.time)
  const day = dayLabel(start, i18next.language)
  if (item.allDay || !item.time) return `${day} · ${t('events.allDay')}`
  return `${day} · ${item.time}–${hhmm(start + (item.durationMinutes ?? 45) * 60_000)}`
}

const shortDay = (day: string) =>
  new Date(localDateTime(day)).toLocaleDateString(i18next.language, {
    month: 'short',
    day: 'numeric',
  })

function itemMeta(t: TFunction, item: CaptureItem) {
  if (item.kind === 'event') return []
  if (item.kind === 'goal') return [categoryLabel(t, item.category), t('voice.metaFullPlan')]
  if (item.kind === 'task')
    return [`→ ${t('nav.inbox')}`, item.when === 'thisWeek' && t('voice.whenThisWeek')]
  const m = item.measure
  return [
    m
      ? m.fast
        ? t('fasting.captureFast', { hours: formatAmount(m.target, i18next.language) })
        : measureChipLabel(m, i18next.language)
      : categoryLabel(t, item.category),
    item.oneOff
      ? t('fasting.schedule.once')
      : item.period === 'days' && item.days?.length
        ? recursDaysLabel(item.days, i18next.language)
        : item.period === 'weekend'
          ? t('inbox.period.weekend')
          : item.period === 'weekday'
            ? t('voice.periodWeekday')
            : item.period === 'weekly'
              ? t('voice.periodWeekly')
              : t('voice.periodDaily'),
    item.timeOfDay && t(`voice.time_${item.timeOfDay}`),
  ]
}

function Chip({
  icon: Icon,
  label,
  active,
  editable,
  onClick,
  onRemove,
}: {
  icon: LucideIcon
  label: string
  active: boolean
  editable: boolean
  onClick?: () => void
  onRemove?: () => void
}) {
  const { t } = useTranslation()
  const pill = cn(
    'flex min-w-0 max-w-[14rem] items-center gap-1 rounded-full font-semibold leading-none',
    active ? 'bg-primary/12 text-primary-text' : 'text-muted-foreground/70',
  )
  const body = (
    <>
      <Icon className="size-3 shrink-0" />
      <span className="truncate">{label}</span>
    </>
  )
  if (!editable) return active ? <span className={cn(pill, 'px-2 py-0.5')}>{body}</span> : null
  return (
    <span className={pill}>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={cn(
            'flex min-w-0 cursor-pointer items-center gap-1 rounded-full py-0.5 pl-2 transition-colors hover:bg-primary/18',
            onRemove ? 'pr-1' : 'pr-2',
          )}
        >
          {body}
        </button>
      ) : (
        <span className="flex min-w-0 items-center gap-1 py-0.5 pl-2 pr-1">{body}</span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('voice.removeChip', { label })}
          title={t('voice.removeChip', { label })}
          className="mr-0.5 flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-primary/20"
        >
          <X className="size-2.5" strokeWidth={3} />
        </button>
      )}
    </span>
  )
}

function TaskChips({
  item,
  relatedEvent,
  editable,
  onWhen,
  onEdit,
}: {
  item: CaptureItem
  relatedEvent?: CaptureItem | null
  editable: boolean
  onWhen?: (when: CaptureItem['when']) => void
  onEdit?: (p: CaptureEdit) => void
}) {
  const { t } = useTranslation()
  const [editingPlace, setEditingPlace] = useState(false)
  const isEvent = item.kind === 'event'
  const day = !isEvent && (item.when === 'today' || item.when === 'tomorrow') ? item.when : null
  const canWhen = editable && !!onWhen && !isEvent
  const canEdit = editable && !!onEdit
  const suggestDay =
    !isEvent && !item.dueDate && relatedEvent?.kind === 'event' ? relatedEvent.dueDate : null
  return (
    <>
      {(day || canWhen) && (
        <Chip
          icon={CalendarCheck}
          label={
            day
              ? t(day === 'today' ? 'mustDo.chipToday' : 'mustDo.chipTomorrow')
              : `+ ${t('mustDo.short')}`
          }
          active={!!day}
          editable={canWhen}
          onClick={
            onWhen && (() => onWhen(day === null ? 'today' : day === 'today' ? 'tomorrow' : null))
          }
          onRemove={day && onWhen ? () => onWhen(null) : undefined}
        />
      )}
      {item.dueDate &&
        (isEvent ? (
          <Chip
            icon={Calendar}
            label={eventWhenLabel(t, { ...item, dueDate: item.dueDate })}
            active
            editable={canEdit}
            // An event without a day is a task.
            onRemove={
              onEdit &&
              (() =>
                onEdit({
                  kind: 'task',
                  dueDate: null,
                  time: null,
                  durationMinutes: null,
                  allDay: false,
                }))
            }
          />
        ) : (
          <Chip
            icon={Calendar}
            label={
              item.dueBefore
                ? t('voice.dueBeforeChip', { date: shortDay(item.dueDate) })
                : captureDueLabel(item.dueDate, item.time, i18next.language)
            }
            active
            editable={canEdit}
            onRemove={
              onEdit &&
              (() => onEdit({ dueDate: null, time: null, durationMinutes: null, dueBefore: false }))
            }
          />
        ))}
      {suggestDay && canEdit && (
        <button
          type="button"
          onClick={() =>
            onEdit?.({ dueDate: suggestDay, time: null, durationMinutes: null, dueBefore: true })
          }
          className="flex cursor-pointer items-center gap-1 rounded-full border border-dashed border-muted-foreground/40 px-2 py-0.5 font-semibold leading-none text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary-text"
        >
          <Plus className="size-3 shrink-0" />
          {t('voice.dueBeforeSuggest', { date: shortDay(suggestDay) })}
        </button>
      )}
      {editingPlace && canEdit ? (
        <span className="flex items-center gap-1 rounded-full bg-primary/12 px-2 py-0.5 text-primary-text">
          <MapPin className="size-3 shrink-0" />
          <PlaceInput
            defaultValue={item.location ?? ''}
            ariaLabel={t('voice.locationLabel')}
            placeholder={t('voice.locationPlaceholder')}
            onCommit={(text, coords) => {
              onEdit?.({ location: text || null, locationCoords: coords })
              setEditingPlace(false)
            }}
            onCancel={() => setEditingPlace(false)}
            className="w-36 bg-transparent font-semibold leading-none outline-none placeholder:text-primary-text/50"
          />
        </span>
      ) : item.location ? (
        <Chip
          icon={MapPin}
          label={item.location}
          active
          editable={canEdit}
          onClick={() => setEditingPlace(true)}
          onRemove={onEdit && (() => onEdit({ location: null, locationCoords: undefined }))}
        />
      ) : (
        canEdit && (
          <button
            type="button"
            onClick={() => setEditingPlace(true)}
            aria-label={t('voice.addLocation')}
            title={t('voice.addLocation')}
            className="flex cursor-pointer items-center rounded-full px-1.5 py-0.5 text-muted-foreground/70 transition-colors hover:bg-primary/12 hover:text-primary-text"
          >
            <MapPin className="size-3" />+
          </button>
        )
      )}
    </>
  )
}

export function CaptureCard({
  item,
  relatedEvent,
  disabled,
  onToggle,
  onRename,
  onOpen,
  onWhen,
  onEdit,
  dense = false,
  live = false,
}: {
  item: CaptureItem
  // Tasks: the event this one was tied to in the same dump.
  relatedEvent?: CaptureItem | null
  disabled: boolean
  dense?: boolean
  onToggle: (selected?: boolean) => void
  onRename: (title: string) => void
  onOpen?: () => void
  // Tasks: cycle the "Must do" day (today → tomorrow → off).
  onWhen?: (when: CaptureItem['when']) => void
  onEdit?: (p: CaptureEdit) => void
  // Heard while the user is still talking: read-only, no checkbox or edits.
  live?: boolean
}) {
  const { t } = useTranslation()
  const isPending = item.status === 'pending'
  const meta = itemMeta(t, item).filter(Boolean).join(' · ')

  return (
    <div
      className={cn(
        'rounded-xl border bg-card transition-colors',
        dense ? 'px-3 py-2.5' : 'px-4 py-3.5',
        item.status === 'created'
          ? 'border-primary/30'
          : item.status === 'error'
            ? 'border-destructive/40'
            : item.status === 'needsPro'
              ? 'border-dashed border-border'
              : 'border-border',
        isPending && !item.selected && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
          {live ? (
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={item.kind}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              >
                <KindIcon kind={item.kind} />
              </motion.span>
            </AnimatePresence>
          ) : isPending ? (
            <button
              type="button"
              role="checkbox"
              aria-checked={item.selected}
              disabled={disabled}
              onClick={() => onToggle()}
              className={cn(
                'flex h-5 w-5 cursor-pointer items-center justify-center rounded',
                item.selected ? 'bg-primary' : 'border border-border',
              )}
            >
              {item.selected && (
                <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />
              )}
            </button>
          ) : item.status === 'creating' ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          ) : item.status === 'created' ? (
            <Check className="h-4 w-4 text-primary-text" strokeWidth={2.5} />
          ) : item.status === 'needsPro' ? (
            <Lock className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <span className="h-4 w-4 rounded-full bg-destructive/30" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={item.kind}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className={cn(
                  'flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                  KIND_PILL[item.kind],
                )}
              >
                {item.kind === 'event' && <CalendarDays className="size-3" strokeWidth={2.5} />}
                {kindLabel(t, item.kind)}
              </motion.span>
            </AnimatePresence>
            {item.status === 'error' && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-destructive">
                {t('voice.statusError')}
              </span>
            )}
            {item.status === 'needsPro' && (
              <button
                type="button"
                onClick={openHabitUpsell}
                className="cursor-pointer rounded-full bg-primary/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-text hover:bg-primary/20"
              >
                {t('habitLimit.needsPro')}
              </button>
            )}
          </div>
          {live ? (
            <p
              className={cn(
                'font-semibold leading-snug text-card-foreground',
                dense ? 'text-sm' : 'text-base',
              )}
            >
              {item.title}
            </p>
          ) : item.status === 'created' && onOpen ? (
            <button
              type="button"
              onClick={onOpen}
              className={cn(
                'w-full cursor-pointer text-left font-semibold leading-snug text-card-foreground underline-offset-2 hover:underline',
                dense ? 'text-sm' : 'text-base',
              )}
            >
              {item.title}
            </button>
          ) : (
            <input
              value={item.title}
              onChange={(e) => onRename(e.target.value)}
              disabled={!isPending || disabled}
              aria-label={t('voice.editTitle')}
              className={cn(
                'w-full rounded bg-transparent font-semibold leading-snug text-card-foreground outline-none focus:bg-muted/40 disabled:opacity-100',
                dense ? 'text-sm' : 'text-base',
              )}
            />
          )}
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
            {item.kind !== 'task' && item.kind !== 'event' && (
              <CategoryDot category={item.category} />
            )}
            {meta && <span className="truncate">{meta}</span>}
            {(item.kind === 'task' || item.kind === 'event') && (
              <TaskChips
                item={item}
                relatedEvent={relatedEvent}
                editable={isPending && !disabled && !live}
                onWhen={onWhen}
                onEdit={onEdit}
              />
            )}
          </div>

          {item.similar && isPending && !live && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-500/10 px-3 py-2">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                {t(
                  item.kind === 'goal'
                    ? 'voice.similarGoal'
                    : item.kind === 'habit'
                      ? 'voice.similarHabit'
                      : item.similar.onToday
                        ? 'voice.similarToday'
                        : 'voice.similarTask',
                  { title: item.similar.title },
                )}
              </p>
              <div className="flex shrink-0 rounded-full border border-amber-500/30 p-0.5 text-xs font-medium">
                {(
                  [
                    [false, 'voice.skip'],
                    [true, 'voice.createAnyway'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={label}
                    type="button"
                    disabled={disabled}
                    onClick={() => onToggle(value)}
                    className={cn(
                      'cursor-pointer rounded-full px-2.5 py-1 transition-colors',
                      item.selected === value
                        ? 'bg-amber-500/20 text-amber-800 dark:text-amber-300'
                        : 'text-amber-700/70 hover:text-amber-700 dark:text-amber-400/70',
                    )}
                  >
                    {t(label)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Items heard so far while the user is still talking; each one pops in as it's
// recognised and rewords in place as the transcript fills out.
function KindIcon({ kind }: { kind: CaptureItem['kind'] }) {
  const Icon = KIND_ICON[kind]
  return <Icon className={cn('size-4 shrink-0', KIND_TEXT[kind])} />
}

const noop = () => {}

const asCard = (item: LiveItem): CaptureItem => ({
  ...liveToCaptureItem(item),
  key: 0,
  selected: true,
  status: 'pending',
  goalId: null,
})

const ENTER_SPRING = { type: 'spring', stiffness: 460, damping: 32, mass: 0.8 } as const

// The motion wrapper both live and review cards sit in. A review card that continues
// a live one shares its layoutId, so it glides from where the live card was instead
// of popping in again.
export function CaptureCardMotion({
  layoutKey,
  appear,
  children,
}: {
  layoutKey?: string
  appear: boolean
  children: ReactNode
}) {
  const reduce = !!useReducedMotion()
  return (
    <motion.li
      layout={reduce ? false : 'position'}
      layoutId={!reduce && layoutKey ? `capture-card-${layoutKey}` : undefined}
      initial={appear ? (reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.96 }) : false}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.97, transition: { duration: 0.16 } }}
      transition={reduce ? { duration: 0.2 } : ENTER_SPRING}
      className="list-none"
    >
      {children}
    </motion.li>
  )
}

// `dense` should match the review cards these become.
export function LiveCaptureItems({ items, dense = true }: { items: LiveItem[]; dense?: boolean }) {
  const { t } = useTranslation()
  const endRef = useRef<HTMLDivElement>(null)
  useStickToEnd(endRef, items.map((i) => i.key).join())
  if (!items.length) return null
  return (
    <div className="mt-4">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
        <span className="size-1.5 animate-pulse rounded-full bg-primary motion-reduce:animate-none" />
        {t('voice.pickingUp')}
      </p>
      <ul className="flex flex-col gap-1.5">
        <AnimatePresence initial={false}>
          {items.map((item) => (
            <CaptureCardMotion key={item.key} layoutKey={item.key} appear>
              <CaptureCard
                item={asCard(item)}
                live
                dense={dense}
                disabled
                onToggle={noop}
                onRename={noop}
              />
            </CaptureCardMotion>
          ))}
        </AnimatePresence>
      </ul>
      <div ref={endRef} />
    </div>
  )
}
